import React, { useState, useCallback, useRef } from "react";
import { TagFilterBar } from "../tag-filter-bar/tag-filter-bar";
import "./multi-tag-text-entry.scss";

export interface MultiTagTextEntryProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  requiredError?: string;
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** Character(s) that split the draft into tags. @default "," */
  separator?: string;
  /** Controlled tags */
  value?: string[];
  /** Uncontrolled initial tags */
  defaultValue?: string[];
  onChange?: (tags: string[]) => void;
}

export interface CommitDraftResult {
  nextTags: string[];
  remainder: string;
}

/**
 * Splits draft on separator and commits segments as tags (deduped) on Enter.
 */
export function commitDraft(
  draft: string,
  separator: string,
  existingTags: string[]
): CommitDraftResult {
  const known = new Set(existingTags);
  const added: string[] = [];

  const appendUnique = (segment: string) => {
    const trimmed = segment.trim();
    if (!trimmed || known.has(trimmed)) return;
    known.add(trimmed);
    added.push(trimmed);
  };

  if (separator && draft.includes(separator)) {
    draft.split(separator).forEach(appendUnique);
  } else {
    appendUnique(draft);
  }

  return {
    nextTags: added.length > 0 ? [...existingTags, ...added] : existingTags,
    remainder: "",
  };
}

let idCounter = 0;

export const MultiTagTextEntry: React.FC<MultiTagTextEntryProps> = ({
  label,
  hint,
  error,
  required = false,
  requiredError = "This field is required",
  size = "sm",
  fullWidth = false,
  placeholder,
  disabled = false,
  className = "",
  id,
  separator = ",",
  value: controlledValue,
  defaultValue = [],
  onChange,
}) => {
  const isControlled = controlledValue !== undefined;
  const [internalTags, setInternalTags] = useState<string[]>(defaultValue);
  const tags = isControlled ? controlledValue : internalTags;

  const [fallbackId] = useState(() => `multi-tag-text-entry-${++idCounter}`);
  const inputId = id ?? fallbackId;

  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const setTags = useCallback(
    (next: string[]) => {
      if (!isControlled) {
        setInternalTags(next);
      }
      onChange?.(next);
    },
    [isControlled, onChange]
  );

  const commitDraftOnEnter = useCallback(
    (nextDraft: string) => {
      const { nextTags } = commitDraft(nextDraft, separator, tags);
      if (
        nextTags.length !== tags.length ||
        nextTags.some((t, i) => t !== tags[i])
      ) {
        setTags(nextTags);
      }
      setDraft("");
    },
    [separator, tags, setTags]
  );

  const isEmpty = tags.length === 0 && draft.trim() === "";
  const requiredValidationError =
    required && touched && isEmpty ? requiredError : undefined;
  const resolvedError = error || requiredValidationError;
  const hasError = !!resolvedError;

  const cls = "multi-tag-text-entry";
  const rootClass = [
    cls,
    `${cls}--${size}`,
    fullWidth ? `${cls}--full-width` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const fieldClass = [
    `${cls}__field`,
    focused ? `${cls}__field--focused` : "",
    hasError ? `${cls}__field--error` : "",
    disabled ? `${cls}__field--disabled` : "",
  ]
    .filter(Boolean)
    .join(" ");

  const message = hasError ? resolvedError : hint;
  const messageClass = hasError ? `${cls}__error` : `${cls}__hint`;

  const handleFieldClick = useCallback(() => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  }, [disabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDraft(e.target.value);
    },
    []
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (draft.trim()) {
          commitDraftOnEnter(draft);
        }
        return;
      }

      if (
        e.key === "Backspace" &&
        draft === "" &&
        tags.length > 0 &&
        !disabled
      ) {
        e.preventDefault();
        setTags(tags.slice(0, -1));
      }
    },
    [draft, tags, disabled, commitDraftOnEnter, setTags]
  );

  const handleTagRemove = useCallback(
    (_label: string, index: number) => {
      if (disabled) return;
      setTags(tags.filter((_, i) => i !== index));
      inputRef.current?.focus();
    },
    [disabled, tags, setTags]
  );

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setFocused(false);
    if (required) {
      setTouched(true);
    }
  }, [required]);

  return (
    <div className={rootClass}>
      {(label != null || required) && (
        <div className={`${cls}__label-row`}>
          {label != null && (
            <label htmlFor={inputId} className={`${cls}__label`}>
              {label}
              {required && (
                <span className={`${cls}__required`} aria-hidden="true">
                  {" "}
                  *
                </span>
              )}
            </label>
          )}
        </div>
      )}

      <div ref={fieldRef} className={fieldClass} onClick={handleFieldClick}>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          className={`${cls}__input`}
          value={draft}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={hasError}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </div>

      {tags.length > 0 && (
        <div className={`${cls}__tags`}>
          <TagFilterBar
            tags={tags}
            showDropdown={false}
            closable={!disabled}
            onTagRemove={handleTagRemove}
          />
        </div>
      )}

      {message != null && <span className={messageClass}>{message}</span>}
    </div>
  );
};

export default MultiTagTextEntry;
